/**
 * The connectivity panel's view model: the state (`state.ts`) and the
 * operator message (`describe.ts`) folded into plain rows the presentational
 * components render (`components/ConnectivityStatus.tsx`). Pure — a function
 * of the state and the clock — so what the panel shows is unit-tested here,
 * and the stories build their states from fixtures of this shape.
 */
import type { CloudLinkState } from '@acp/api-contract/health.js';
import { describeConnectivity, type ConnectivitySummary } from './describe.js';
import { type ConnectivityState, localOffer, type Reach, type SocketState } from './state.js';

export type EndpointRow = {
  readonly id: string;
  readonly kind: 'cloud' | 'node';
  readonly label: string;
  readonly url: string;
  readonly primary: boolean;
  readonly reach: Reach;
  readonly latencyMs: number | null;
  readonly version: string | null;
  readonly stage: string | null;
  /** A node's own site link; null for the cloud or before it answered. */
  readonly cloudLink: CloudLinkState | null;
  readonly outboxDepth: number | null;
  /** "12 s ago" since the last answer, when it is not answering now; null otherwise. */
  readonly lastSeen: string | null;
  readonly error: string | null;
};

export type CloudViewRow = { readonly siteId: string; readonly nodeId: string; readonly role: string; readonly linkState: string; readonly lastSeen: string };

export type ConnectivityView = {
  readonly summary: ConnectivitySummary;
  readonly primary: { readonly id: string; readonly kind: 'cloud' | 'node'; readonly label: string; readonly latencyMs: number | null };
  readonly socket: SocketState;
  readonly endpoints: readonly EndpointRow[];
  /** "Switch to local" (PLAN §10.2): the node to offer, or null. */
  readonly offerLocal: { readonly id: string; readonly label: string } | null;
  /** The operator chose a primary; "use the default" undoes it. */
  readonly chosen: boolean;
  /** The cloud's view of the tenant's sites and nodes; null when it could not be read. */
  readonly cloudView: readonly CloudViewRow[] | null;
};

/** A duration for people: `4 s`, `3 min`, `2 h`. */
export function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${String(s)} s ago`;
  const min = Math.round(s / 60);
  if (min < 60) return `${String(min)} min ago`;
  return `${String(Math.round(min / 60))} h ago`;
}

export function connectivityView(state: ConnectivityState, nowMs: number): ConnectivityView {
  const endpoints: EndpointRow[] = state.endpoints.map((s) => ({
    id: s.endpoint.id,
    kind: s.endpoint.kind,
    label: s.endpoint.label,
    url: s.endpoint.apiUrl,
    primary: s.endpoint.id === state.primaryId,
    reach: s.reach,
    latencyMs: s.latencyMs,
    version: s.version,
    stage: s.stage,
    cloudLink: s.node?.cloudLink ?? null,
    outboxDepth: s.node?.outboxDepth ?? null,
    lastSeen: s.reach !== 'up' && s.lastOkAtMs !== null ? ago(nowMs - s.lastOkAtMs) : null,
    error: s.error,
  }));
  const primaryRow = endpoints.find((e) => e.primary) ?? endpoints[0];
  const offerId = localOffer(state);
  const offer = offerId === null ? null : (endpoints.find((e) => e.id === offerId) ?? null);
  return {
    summary: describeConnectivity(state),
    primary: primaryRow === undefined ? { id: state.primaryId, kind: 'cloud', label: '?', latencyMs: null } : { id: primaryRow.id, kind: primaryRow.kind, label: primaryRow.label, latencyMs: primaryRow.latencyMs },
    socket: state.socket,
    endpoints,
    offerLocal: offer === null ? null : { id: offer.id, label: offer.label },
    chosen: state.chosenId !== null,
    cloudView:
      state.cloudView === null
        ? null
        : state.cloudView.sites.flatMap((site) =>
            site.nodes.map((n) => ({ siteId: site.siteId, nodeId: n.nodeId, role: n.role, linkState: n.linkState, lastSeen: n.lastSeen === null ? 'never' : ago(nowMs - Date.parse(n.lastSeen)) })),
          ),
  };
}
